import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { LoadingComponent } from "@digi-america/shared";
import { FormGroupComponent } from "../product-editor/form-group.component";
import { PercentInputComponent } from '../product-editor/percent-input.component';
import { DealDeskService } from '../../services/deal-desk.service';
import { RequestCreateDealDesk } from '../../types/dai-server/deal-desk/deal-desk-requests';
import { DealDeskPriceType, ProcurementStage, PROCUREMENT_STAGES } from '../../types/dai-server/deal-desk/enums';
import { ControlsOfExcept } from '../../types/ControlsOf';
import { NgbDateStruct, NgbInputDatepicker } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-deal-desk-create',
  imports: [FormGroupComponent, ReactiveFormsModule, LoadingComponent, PercentInputComponent, NgbInputDatepicker],
  templateUrl: './deal-desk-create.component.html',
  styleUrl: './deal-desk-create.component.scss',
  standalone: true
})
export class DealDeskCreateComponent implements OnInit {
  public form?: FormGroup<
    ControlsOfExcept<RequestCreateDealDesk, 'lastDecisionMakerContact'> &
    { lastDecisionMakerContact: FormControl<NgbDateStruct | null> }
  >;
  public submitting = false;
  public readonly procurementStages = PROCUREMENT_STAGES;
  public readonly todayDate = new Date();

  constructor(private fb: NonNullableFormBuilder, private router: Router, private dealDeskService: DealDeskService) { }

  ngOnInit(): void {
    this.form = this.fb.group({
      customerName: this.fb.control('', [Validators.required, Validators.maxLength(255)]),
      customerAddress: this.fb.control('', [Validators.required, Validators.maxLength(255)]),
      customerTelephone: this.fb.control('', [Validators.required, Validators.maxLength(50)]),
      decisionMakerPOC: this.fb.control('', [Validators.required, Validators.maxLength(255)]),
      currentOpportunityDescription: this.fb.control('', [Validators.required, Validators.minLength(1)]),
      competitiveSituationDescription: this.fb.control('', [Validators.required, Validators.minLength(1)]),
      priceType: this.fb.control(DealDeskPriceType.list, Validators.required),
      discountRequested: this.fb.control(0, [Validators.min(0), Validators.max(100)]),
      procurementStage: this.fb.control(ProcurementStage.quotation, Validators.required),
      lastDecisionMakerContact: this.fb.control<NgbDateStruct | null>(null),
    });
  }

  async submit() {
    if (this.form && this.form.valid && !this.submitting) {
      this.submitting = true;
      try {
        const { lastDecisionMakerContact, ...rest } = this.form.getRawValue();
        const data: RequestCreateDealDesk = {
          ...rest,
          lastDecisionMakerContact: this.toDateOnly(lastDecisionMakerContact)
        };
        await firstValueFrom(this.dealDeskService.createDealDesk(data));
        this.router.navigate(['/', 'deal-desk']);
      } finally {
        this.submitting = false;
      }
    }
  }

  // The date picked is a calendar date, so it is sent as yyyy-MM-dd rather than
  // an instant - going through toISOString() would shift the day for anyone
  // east of UTC.
  private toDateOnly(date: NgbDateStruct | null): string | null {
    if (!date) {
      return null;
    }
    const pad = (value: number) => value.toString().padStart(2, '0');
    return `${date.year}-${pad(date.month)}-${pad(date.day)}`;
  }
}
